import { createFileRoute } from '@tanstack/react-router'

import { useCreateSalesChannelType } from '@/api/api-mutations'
import type { FulfillmentProviderType } from '@/api/common-types'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/sales-channels/types/create')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.SalesChannelTypes.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewSalesChannelTypeForm />
		</RouteModal>
	)
}

function NewSalesChannelTypeForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()

	const createSalesChannelType = useCreateSalesChannelType()

	const form = useAppForm({
		defaultValues: {
			name: '',
			fulfillmentProvider: '' as '' | FulfillmentProviderType,
			preauthorizationAmountValue: '',
			allowCompletingUnderpaidOrders: false
		},
		onSubmit: async ({ value }) => {
			const result = await createSalesChannelType({
				body: {
					name: value.name,
					fulfillmentProvider: value.fulfillmentProvider || undefined,
					allowCompletingUnderpaidOrders: value.allowCompletingUnderpaidOrders,
					...(value.preauthorizationAmountValue
						? {
								preauthorizationAmount: {
									value: Number(value.preauthorizationAmountValue),
									precision: 0
								}
							}
						: {})
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.SalesChannelTypes.CreateSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.SalesChannelTypes.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Settings.SalesChannelTypes.Create.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='fulfillmentProvider'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannelTypes.Column.FulfillmentProvider()}
									options={[
										{
											value: '',
											label: L.Settings.SalesChannelTypes.FulfillmentProviderNone()
										},
										{
											value: 'MERCHANT_APP',
											label: L.Enum.FulfillmentProvider('MERCHANT_APP')
										}
									]}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='preauthorizationAmountValue'
							validators={{
								onChange: ({ value }) => {
									if (!value) return undefined
									const parsed = Number(value)
									return !Number.isFinite(parsed) || parsed < 0
										? L.Settings.SalesChannelTypes.InvalidPreauthorizationAmount()
										: undefined
								}
							}}
							children={field => (
								<field.TextField
									label={L.Settings.SalesChannelTypes.Column.PreauthorizationAmount()}
									placeholder={L.General.Optional()}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='allowCompletingUnderpaidOrders'
							children={field => (
								<field.BooleanField
									label={L.Settings.SalesChannelTypes.Column.AllowCompletingUnderpaidOrders()}
									hint={L.Settings.SalesChannelTypes.AllowCompletingUnderpaidOrdersHint()}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
